import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import ModalBase from "@/components/ui/modal/modal-base";
import { useToast } from "@/providers/toast-provider";
import { GroupMultiSelect } from "@/shared/components/group-multi-select";
import { useGroupsQuery } from "@/shared/hooks";
import { useUpdateUserMutation } from "../hooks";
import { type EditUserFormValues, editUserSchema } from "../schemas";
import type { EditUserProps } from "../types";
import { maskCPF } from "../utils/formatters";

export const EditUser = ({
	isOpen,
	onClose,
	user,
	onSuccess,
}: EditUserProps) => {
	const { showToast } = useToast();
	const { mutateAsync } = useUpdateUserMutation();
	const { data: groups = [], isLoading: isGroupsLoading } = useGroupsQuery({
		enabled: isOpen,
	});

	const roleLabels: Record<string, string> = {
		Driver: "Motorista",
		Manager: "Gestor de frota",
		Admin: "Administrador",
		TelemetryAdmin: "Telemetria Admin",
		TelemetryMember: "Telemetria Membro",
	};

	const {
		control,
		handleSubmit,
		formState: { errors },
		reset,
		setValue,
		watch,
		trigger,
	} = useForm<EditUserFormValues>({
		resolver: zodResolver(editUserSchema),
		defaultValues: {
			name: "",
			email: "",
		},
	});

	useEffect(() => {
		if (user) {
			reset({
				name: user.name,
				email: user.email,
				group_ids: user.groups.map((g) => g.id),
			});
		} else {
			reset();
		}
	}, [user, reset]);

	const onSubmit = async (data: EditUserFormValues) => {
		if (!user) {
			return;
		}

		try {
			await mutateAsync({
				userId: user.id,
				data: {
					name: data.name,
					email: data.email,
					groupIds: data.group_ids,
				},
			});

			showToast({
				title: "Usuário atualizado",
				variant: "success",
				description: "As informações foram salvas com sucesso.",
			});

			onSuccess?.();
		} catch {
			showToast({
				title: "Erro ao atualizar",
				variant: "error",
				description: "Não foi possível salvar as alterações. Tente novamente.",
			});
		}
	};

	return (
		<ModalBase
			actions={[
				{
					label: "Cancelar",
					onClick: () => {
						reset();
						onClose();
					},
					color: "secondary",
				},
				{ label: "Salvar", onClick: handleSubmit(onSubmit), color: "primary" },
			]}
			height="auto"
			icon={false}
			isOpen={isOpen}
			onClose={() => {
				reset();
				onClose();
			}}
			title="Editar usuário"
			width={494}
		>
			<div className="mb-8 flex flex-col gap-5 px-0.5">
				<div className="relative">
					<Input
						hint={errors.name?.message}
						isInvalid={!!errors.name}
						label="Nome"
						onBlur={() => {
							const value = watch("name")?.trim();
							setValue("name", value);
							trigger("name");
						}}
						onChange={(value) => {
							const sanitized = value
								.replace(/^\s+/g, "")
								.replace(/\s{2,}/g, " ");

							if (sanitized.length <= 50) {
								setValue("name", sanitized);
							} else {
								setValue("name", sanitized.slice(0, 50));
							}
						}}
						placeholder="Nome completo"
						value={watch("name")}
					/>
				</div>

				<Input
					isDisabled
					label="CPF"
					placeholder="000.000.000-00"
					value={maskCPF(user?.cpf)}
				/>

				<div className="relative">
					<Input
						hint={errors.email?.message}
						isInvalid={!!errors.email}
						label="E-mail"
						onBlur={() => {
							const value = watch("email")?.trim();
							setValue("email", value);
							trigger("email");
						}}
						onChange={(value) => {
							const sanitized = value.replace(/\s+/g, "").toLowerCase();

							setValue("email", sanitized);
						}}
						placeholder="Email válido para acesso"
						value={watch("email")}
					/>
				</div>

				<Select
					isDisabled={true}
					items={
						user?.roles.map((role) => ({
							id: role,
							label: roleLabels[role] || role,
						})) || []
					}
					label="Tipo de usuário"
					placeholder="Nenhum perfil"
					selectedKey={user?.roles[0]}
				>
					{(item) => <SelectItem id={item.id} label={item.label} />}
				</Select>

				<div className="relative">
					<Controller
						control={control}
						name="group_ids"
						render={({ field }) => (
							<GroupMultiSelect
								groups={groups}
								hint={errors.group_ids?.message}
								isInvalid={!!errors.group_ids}
								isLoading={isGroupsLoading}
								label="Grupo"
								onChange={field.onChange}
								value={field.value ?? []}
							/>
						)}
					/>
				</div>
			</div>
		</ModalBase>
	);
};
